// A lista GRUDADA no pé: quem está no fim de uma conversa continua no fim quando o conteúdo cresce (o cartão do site,
// a imagem que carrega ou falha, o anexo, a mensagem nova), e quem sobe para ler o histórico não é puxado de volta.
// Serve a qualquer lista de conversa (o chat agora; o atendimento da equipe e o portal no PR 3): ela entrega o elemento
// que rola e o do conteúdo, e só ela leva ao fim.
//
// 🔴 Por que a LISTA, e não o cartão (T-R19, 28/10): a regra 1 do `useEntradaSemPulo` decidia "estava no pé" pela conta
// de cada cartão (`scrollHeight − delta − scrollTop − clientHeight ≤ 8`). Três rodadas não fecharam: N cartões no mesmo
// commit não enxergam o crescimento uns dos outros; somar os deltas (o lote) contava o que a âncora do navegador já
// compensara (puxava ao pé quem lia a 300–1000 px dele); e a altura do cartão (237,23 px no app) não é o que a lista
// cresce (241,23: a margem), então 13 cartões ficavam 965 e 2171 px acima do fim. Aqui não há conta por cartão:
//
// - O estado "grudado" muda só com `scroll` (da pessoa ou programático): no pé (≤ 8 px) gruda; SUBIU longe do pé,
//   solta; desceu sem chegar ao pé (a âncora do navegador compensando o que cresceu acima, a página anterior somada ao
//   `scrollTop`), mantém. O conteúdo crescer não dispara `scroll` com o `scrollTop` menor, então nunca solta; encolher
//   no pé recua o `scrollTop`, mas para o novo pé, e o pé gruda.
// - Um `ResizeObserver` no conteúdo E no próprio rolável (o campo de texto que cresce, a barra do cartão, o teclado do
//   celular): mudou de tamanho e está grudado → `scrollTop = scrollHeight`, instantâneo, antes de pintar. Quantos
//   cartões, de que altura, com que margem: não importa, o que vale é o fim.
// - Suspensa (outro dono rola a lista: o salto até uma mensagem), não leva ao pé; ao sair da suspensão o estado sai da
//   geometria (o salto pode ter terminado no pé).
//
// - Âncora própria onde o navegador não ancora (Safari; `overflow-anchor: none`): com a lista solta, o que cresce ACIMA
//   do que se lê empurraria a leitura. A lista guarda o elemento que se lê (`elementoLido`) e o topo dele no rolável; no
//   aviso de tamanho, devolve ao `scrollTop` o quanto ele andou. É exato (mede o elemento, não soma alturas: a margem do
//   cartão entra sozinha) e vale para o cartão, a imagem e o anexo. O cartão ainda soma a própria altura antes de
//   pintar (`compensar`, a regra 2 dele): com a pessoa rolando, o `scroll` dela remarca a âncora antes do aviso, e é
//   essa soma que fica (perde-se só a margem). Onde o navegador ancora (Chrome, Firefox), ele já compensou e a lista não
//   mexe.
//
// O registro por rolável (`listaGrudadaDe`) é o que diz ao `useEntradaSemPulo` que a lista cuida de tudo isso. Rolar por
// código funciona com `overflow-y: hidden` (o estado inicial do viewport do Radix, antes do efeito dele), então a lista
// liga no viewport que entrega, sem olhar o estilo.

/** Até quantos pixels do fim da lista ainda contam como "no pé". */
export const FOLGA_DO_PE_PX = 8;

/**
 * Quanto o `scrollTop` tem de recuar, longe do pé, para contar como "a pessoa subiu": abaixo disso é arredondamento
 * (o navegador guarda `scrollTop` fracionário), não gente.
 */
const RECUO_DE_QUEM_SOBE_PX = 1;

/** Até quantos níveis a âncora própria desce dentro de quem atravessa o topo visível. */
const PROFUNDIDADE_DA_ANCORA = 12;

/** A lista que um nó tem por cima, como o `useEntradaSemPulo` a enxerga. */
export interface ListaGrudada {
  /** O elemento que rola (o viewport da lista). */
  readonly rolavel: HTMLElement;
  /** A lista está no pé e se mantém lá sozinha quando o conteúdo cresce. */
  readonly grudado: boolean;
  /** Outro dono rola a lista agora (o salto): ela não leva ao pé. */
  readonly suspenso: boolean;
  /**
   * A regra 2 de quem cresceu acima do que se lê (o cartão, que só conhece a própria altura): leva o scrollTop JÁ,
   * antes de pintar, sem contar como a pessoa rolando. Idempotente: com a âncora própria valendo, corrige só o que o
   * elemento lido ainda está fora do lugar (no máximo `px`); o aviso de tamanho corrige o resto (a margem). Sem âncora
   * valendo, soma `px`. Grudada, vai ao pé.
   */
  compensar(px: number): void;
}

export interface ControleDoPe extends ListaGrudada {
  /** Leva ao fim e gruda (abrir a conversa, mensagem nova): `scrollTo` até o `scrollHeight`, no comportamento pedido. */
  irAoPe(comportamento?: ScrollBehavior): void;
  /** Liga ou desliga a suspensão; ao desligar, o estado sai da geometria. Repetir o mesmo valor não faz nada. */
  suspender(suspenso: boolean): void;
  /** Tira o ouvinte, desconecta o observador e sai do registro. Idempotente. */
  desligar(): void;
}

/** Quanto falta para o fim do conteúdo, em px (0 no pé). */
export function distanciaDoPe(rolavel: HTMLElement): number {
  return rolavel.scrollHeight - rolavel.scrollTop - rolavel.clientHeight;
}

const noPe = (rolavel: HTMLElement) => distanciaDoPe(rolavel) <= FOLGA_DO_PE_PX;

/**
 * O navegador compensa sozinho o conteúdo que cresce acima do que se lê? Chrome e Firefox sim (âncora de
 * rolagem); o Safari não conhece `overflow-anchor`. Um rolável com `overflow-anchor: none` também desliga.
 * `null` = a janela, que se mede pelo `document.scrollingElement`.
 */
export function navegadorAncoraRolagem(rolavel: HTMLElement | null): boolean {
  if (typeof CSS === "undefined" || typeof CSS.supports !== "function") return false;
  if (!CSS.supports("overflow-anchor", "auto")) return false;
  const alvo = rolavel ?? document.scrollingElement ?? document.documentElement;
  return getComputedStyle(alvo).getPropertyValue("overflow-anchor") !== "none";
}

/**
 * O elemento que a pessoa está lendo, para a âncora própria: a partir do conteúdo, o primeiro filho que ainda aparece
 * (o fundo abaixo do topo visível); se ele começa dentro da área visível, é ele; se atravessa o topo, desce nele (a
 * bolha cujo cartão entra acima do topo: o texto dela, à vista, é o que não pode andar). Parecido com o que o Chrome
 * escolhe. Elemento sem caixa (`display: none`) não conta. `null` = nada à vista.
 */
export function elementoLido(conteudo: Element, topoVisivel: number): Element | null {
  let pai: Element = conteudo;
  for (let nivel = 0; nivel < PROFUNDIDADE_DA_ANCORA; nivel++) {
    let achado: Element | null = null;
    let topoDoAchado = 0;
    for (const filho of Array.from(pai.children)) {
      const r = filho.getBoundingClientRect();
      if (r.width === 0 && r.height === 0) continue;
      if (r.bottom > topoVisivel) {
        achado = filho;
        topoDoAchado = r.top;
        break;
      }
    }
    if (!achado) return pai === conteudo ? null : pai;
    if (topoDoAchado >= topoVisivel || achado.children.length === 0) return achado;
    pai = achado;
  }
  return pai;
}

const listas = new WeakMap<Element, ControleDoPe>();

/**
 * Gruda a lista no pé: o estado nasce da geometria (no pé, ou a lista mais curta que o rolável, = grudado). Cada
 * chamada pede o seu `desligar`. Um segundo controle no mesmo rolável toma o lugar do primeiro no registro.
 */
export function grudarNoPe(rolavel: HTMLElement, conteudo: HTMLElement | null): ControleDoPe {
  let grudado = noPe(rolavel);
  let suspenso = false;
  let ligado = true;
  // O `scrollTop` do último `scroll` (ou da última escrita nossa): é contra ele que se vê se a pessoa subiu.
  let topoAnterior = rolavel.scrollTop;
  // A âncora própria (só onde o navegador não ancora): o elemento que se lê, o topo dele em relação ao topo do rolável
  // (o rolável que muda de lugar na página, como um aviso que aparece acima do chat, não é crescimento) e o scrollTop
  // de então.
  const ancoraPropria = conteudo !== null && !navegadorAncoraRolagem(rolavel);
  let ancora: { el: Element; topo: number; scrollTop: number } | null = null;
  const topoNoRolavel = (el: Element) =>
    el.getBoundingClientRect().top - rolavel.getBoundingClientRect().top;

  // A marca ainda descreve o que se lê: o elemento está no documento e ninguém rolou desde ela. Uma exceção: o conteúdo
  // ENCOLHEU acima e o navegador prendeu o scrollTop no novo máximo (o leitor estava a menos do que encolheu do pé). Esse
  // recuo não é a pessoa nem quem chama, e o topo do elemento no rolável já o desconta (medido no Chromium sem âncora:
  // 3 cartões de 237 → 54 com o leitor a 300 px; sem a exceção a leitura andava 300 px).
  const marcaVale = (): boolean => {
    if (!ancora || !ancora.el.isConnected) return false;
    const st = rolavel.scrollTop;
    if (st === ancora.scrollTop) return true;
    return (
      st < ancora.scrollTop && Math.abs(st - (rolavel.scrollHeight - rolavel.clientHeight)) < 1
    );
  };

  const marcarAncora = () => {
    if (!ancoraPropria || !conteudo) return;
    const el = elementoLido(conteudo, rolavel.getBoundingClientRect().top);
    ancora = el ? { el, topo: topoNoRolavel(el), scrollTop: rolavel.scrollTop } : null;
  };

  const aoRolar = () => {
    const topo = rolavel.scrollTop;
    // 🔴 O eco da NOSSA escrita (o `scroll` chega no quadro seguinte, com o valor que escrevemos) não remarca a âncora:
    // se um cartão entrou entre a escrita e o eco, remarcar agora engoliria o deslocamento dele.
    const eco = topo === topoAnterior;
    if (noPe(rolavel)) grudado = true;
    else if (topo < topoAnterior - RECUO_DE_QUEM_SOBE_PX) grudado = false;
    topoAnterior = topo;
    // A pessoa (ou quem chama, como o "Carregar mais", que já somou a página ao scrollTop) mudou o que se lê.
    if (!eco) marcarAncora();
  };

  const aoMudarDeTamanho = () => {
    // `ligado`: um aviso já enfileirado no quadro em que a lista desligou não mexe em nada.
    if (!ligado || suspenso) return;
    if (grudado) {
      // Instantâneo (o `smooth` deixaria quadros longe do pé) e por atribuição: o navegador prende ao novo máximo. Uma
      // rolagem suave nossa em andamento (a abertura) é interrompida aqui e termina no fim NOVO.
      rolavel.scrollTop = rolavel.scrollHeight;
      topoAnterior = rolavel.scrollTop;
      return;
    }
    // Solta, sem âncora do navegador: o que se lê andou na tela (algo cresceu ou encolheu acima dele) e volta.
    // 🔴 Só se ninguém rolou desde a marca: quem chama pode ter somado a página anterior ao scrollTop no mesmo commit
    // (o "Carregar mais"), e no Chromium este aviso chega ANTES do `scroll` dessa escrita (medido em 28/10). Corrigir aí
    // desfaria a soma: a marca é velha, e o elemento marcado pode até ser outro (os grupos por dia têm `key` de índice).
    if (ancora && marcaVale()) {
      const desvio = topoNoRolavel(ancora.el) - ancora.topo;
      if (Math.abs(desvio) >= 0.5) {
        rolavel.scrollTop += desvio;
        topoAnterior = rolavel.scrollTop;
      }
    }
    marcarAncora();
  };

  rolavel.addEventListener("scroll", aoRolar, { passive: true });
  const observador =
    typeof ResizeObserver === "undefined" ? undefined : new ResizeObserver(aoMudarDeTamanho);
  observador?.observe(rolavel);
  if (conteudo) observador?.observe(conteudo);
  marcarAncora();

  const controle: ControleDoPe = {
    rolavel,
    get grudado() {
      return grudado;
    },
    get suspenso() {
      return suspenso;
    },
    compensar(px: number) {
      if (!ligado || !Number.isFinite(px) || px === 0) return;
      if (grudado) {
        rolavel.scrollTop = rolavel.scrollHeight;
        topoAnterior = rolavel.scrollTop;
        return;
      }
      // 🔴 NB-1 (revisão de 28/10): idempotente. Com a marca valendo (ninguém rolou desde ela), corrige só o que o
      // elemento lido AINDA está fora do lugar, e nunca mais que o `px` de quem chama, no mesmo sentido. O cartão JÁ
      // MONTADO que muda de altura (a imagem falha, 237 → 54) avisa DEPOIS do observador da lista, que já corrigiu tudo:
      // somar o `px` às cegas dobrava (a leitura andava 182,77 px). O teto do `px` vale para a página anterior que
      // entra junto no topo: quem soma o resto dela é quem chama ("Carregar mais").
      let aplicar = px;
      if (ancora && marcaVale()) {
        const medido = topoNoRolavel(ancora.el) - ancora.topo;
        aplicar =
          Math.sign(medido) === Math.sign(px)
            ? Math.sign(px) * Math.min(Math.abs(px), Math.abs(medido))
            : 0;
      } else {
        // Sem marca valendo (nada à vista, ou alguém escreveu no scrollTop sem o `scroll` chegar): o `px` de reserva,
        // e a marca velha não serve mais a ninguém (o aviso remarca).
        ancora = null;
      }
      if (Math.abs(aplicar) < 0.5) return;
      rolavel.scrollTop += aplicar;
      topoAnterior = rolavel.scrollTop;
      // A marca segue valendo (o que se lê tem de voltar ao mesmo topo); só o scrollTop dela anda junto, para o aviso
      // saber que esta escrita foi nossa e corrigir o resto.
      if (ancora) ancora.scrollTop = rolavel.scrollTop;
    },
    irAoPe(comportamento: ScrollBehavior = "auto") {
      // Gruda ANTES de rolar: o que crescer durante a rolagem suave (o cartão que chega na abertura) já vai ao fim
      // novo, e os `scroll` da animação (scrollTop maior a cada quadro) mantêm o estado.
      grudado = true;
      rolavel.scrollTo({ top: rolavel.scrollHeight, behavior: comportamento });
    },
    suspender(sim: boolean) {
      if (sim === suspenso) return;
      suspenso = sim;
      if (sim) return;
      grudado = noPe(rolavel);
      topoAnterior = rolavel.scrollTop;
      marcarAncora();
    },
    desligar() {
      if (!ligado) return;
      ligado = false;
      rolavel.removeEventListener("scroll", aoRolar);
      observador?.disconnect();
      if (listas.get(rolavel) === controle) listas.delete(rolavel);
    },
  };
  listas.set(rolavel, controle);
  return controle;
}

/**
 * A lista grudada que tem o nó por dentro (o ancestral registrado mais próximo; o próprio nó não conta), ou `null`
 * fora de uma: o fio, o feed, a lista que ainda não ligou.
 */
export function listaGrudadaDe(no: Element): ListaGrudada | null {
  for (let atual = no.parentElement; atual; atual = atual.parentElement) {
    const lista = listas.get(atual);
    if (lista) return lista;
  }
  return null;
}
